import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BRAND, PRIMARY_PHONE, WHATSAPP_URL } from "@/constants/content";
import { useBooking } from "@/contexts/BookingContext";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Medical Equipment", to: "/medical-equipment" },
  { label: "Locations", to: "/locations" },
  { label: "Blogs", to: "/blogs" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar({ transparentOnHero = false }) {
  const { open } = useBooking();
  const [openMenu, setOpenMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpenMenu(false); }, [location.pathname]);

  const isActive = (to) => location.pathname === to || (to !== "/" && location.pathname.startsWith(to));
  const useSolid = !transparentOnHero || scrolled;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        useSolid ? "bg-white/85 backdrop-blur-xl border-b border-slate-100 shadow-[0_1px_20px_rgba(15,23,42,0.04)]" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="container-page">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo">
            <img src={BRAND.logo} alt="KinFlex logo" className="h-10 w-10 rounded-xl object-cover ring-1 ring-slate-200" />
            <div className="leading-tight">
              <div className="font-display font-bold text-slate-900 text-[15px] tracking-tight">KinFlex</div>
              <div className="text-[9px] uppercase tracking-[0.2em] font-semibold" style={{ color: '#0A84FF' }}>Home Healthcare</div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`px-3 py-2 text-[13px] font-medium rounded-full transition-colors ${
                  isActive(l.to) ? "text-[#0A84FF] bg-blue-50" : "text-slate-600 hover:text-[#0A84FF] hover:bg-slate-50"
                }`}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <a
              href={`tel:${PRIMARY_PHONE}`}
              className="hidden lg:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[13px] font-semibold text-slate-700 hover:text-[#0A84FF] transition-colors"
              data-testid="nav-call-btn"
            >
              <Phone className="h-4 w-4" /> Call Now
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white px-3.5 py-2 text-[13px] font-semibold shadow-sm hover:shadow-md transition-all"
              data-testid="nav-whatsapp-btn"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <Button
              onClick={() => open()}
              className="rounded-full bg-[#0A84FF] hover:bg-[#0070d9] text-white px-5 h-9 text-[13px] font-semibold shadow-md hover:shadow-lg transition-all"
              data-testid="nav-book-btn"
            >
              Book Appointment <ChevronRight className="h-4 w-4 ml-0.5" />
            </Button>
          </div>

          <button
            className="xl:hidden md:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100"
            onClick={() => setOpenMenu(!openMenu)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {openMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <button
            className="hidden md:inline-flex xl:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100"
            onClick={() => setOpenMenu(!openMenu)}
            aria-label="Toggle menu"
          >
            {openMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {openMenu && (
          <div className="xl:hidden pb-5 space-y-1" data-testid="nav-mobile-panel">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpenMenu(false)}
                className={`block px-3 py-2.5 text-sm font-medium rounded-lg ${
                  isActive(l.to) ? "bg-blue-50 text-[#0A84FF]" : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <div className="grid grid-cols-2 gap-2 pt-3 px-1">
              <a href={`tel:${PRIMARY_PHONE}`} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-slate-900 text-white text-sm font-semibold py-2.5" data-testid="nav-mobile-call">
                <Phone className="h-4 w-4" /> Call Now
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#25D366] text-white text-sm font-semibold py-2.5">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
              <Button onClick={() => { open(); setOpenMenu(false); }} className="col-span-2 rounded-full bg-[#0A84FF] hover:bg-[#0070d9] text-white h-10 font-semibold" data-testid="nav-mobile-book-btn">
                Book Appointment
              </Button>
            </div>
            <Link to="/admin/login" className="block text-center text-xs text-slate-500 pt-3 hover:text-[#0A84FF]">Admin Login</Link>
          </div>
        )}
      </div>
    </header>
  );
}
